import type { IconSize } from '../../../../../../../mads/icons/Icon';

import { type BoxRef, Box, Button, Divider, Flex, Text } from '../../../../../../../mads';
import { Check, Close, Question } from '../../../../../../../mads/icons';
import { useI18n } from '../../../../../../../shared/I18n';
import { setUniversalFocus } from '../../../../../../../shared/util/AccessibilityFocusManager';
import { useViewjobProctorAllocation } from '../../../../../../proctor/__generated__/ViewjobProctorContext';
import { MobVjMatchQualificationA11yLabel } from '../../../../../../proctor/__generated__/ViewjobProctorSpecifications';

import { colors } from '@indeed/ifl-tokens';
import { borderRadius } from '@indeed/ifl-tokens/react-native';
import React, { useImperativeHandle, useRef } from 'react';
import { StyleSheet } from 'react-native';

/**
 * Variants for MatchQualification component
 * - Matching: Green check icon, indicates the user meets this qualification
 * - Missing: Orange question icon, indicates the qualification is not confirmed
 * - Mismatching: Red ban icon, indicates the user does not meet this qualification
 */
export enum MatchQualificationVariant {
    Matching = 'matching',
    Missing = 'missing',
    Mismatching = 'mismatching',
}

/**
 * Imperative handle exposed by MatchQualification so parents can move keyboard
 * and screen-reader focus to whichever interactive control is currently
 * rendered for the qualification (Undo when the user has interacted, otherwise
 * Confirm). Used to satisfy WCAG 2.4.3 Focus Order when buttons swap out or
 * additional items are revealed dynamically.
 */
export interface MatchQualificationRef {
    focus: () => void;
}

/**
 * Props for MatchQualification component
 */
export interface MatchQualificationProps {
    /** The variant determining the icon and styling */
    variant: MatchQualificationVariant;
    /** Main qualification text */
    primaryText: string;
    /** Whether the qualification is required */
    required: boolean;
    /** Optional custom CTA text (defaults to "Add") */
    ctaText?: string;
    /** Optional callback when Confirm is pressed */
    onConfirm: () => void;
    /** Optional callback when Reject is pressed */
    onReject: () => void;
    /** Optional callback when Undo is pressed */
    onUndo?: () => void;
    /** Imperative handle for moving focus to the row's interactive control */
    ref?: React.Ref<MatchQualificationRef>;
}

interface VariantConfig {
    icon: React.ComponentType<{ color: string; size: IconSize }>;
    iconColor: string;
    backgroundColor: string;
    textColor?: string;
    iconAriaLabel: string;
}

const getVariantConfigs: (
    gt: ReturnType<typeof useI18n>,
) => Record<MatchQualificationVariant, VariantConfig> = (gt: ReturnType<typeof useI18n>) => ({
    [MatchQualificationVariant.Matching]: {
        icon: Check,
        iconColor: colors.foreground.status.success['[base]'],
        backgroundColor: colors.background.status.success.weaker,
        textColor: colors.foreground.status.success['[base]'],
        iconAriaLabel: gt.gettext('Matching qualification'),
    },
    [MatchQualificationVariant.Missing]: {
        icon: Question,
        iconColor: colors.foreground['[base]'],
        backgroundColor: colors.background.weak,
        textColor: colors.foreground['[base]'],
        iconAriaLabel: gt.gettext('Missing qualification'),
    },
    [MatchQualificationVariant.Mismatching]: {
        icon: Close,
        iconColor: colors.foreground.status.warning['[base]'],
        backgroundColor: colors.background.status.warning.weaker,
        textColor: colors.foreground.status.warning['[base]'],
        iconAriaLabel: gt.gettext('Mismatching qualification'),
    },
});

export const MatchQualification: React.FC<MatchQualificationProps> = ({
    variant,
    primaryText,
    required,
    onConfirm,
    onReject,
    onUndo,
    ref,
}) => {
    const gt = useI18n();
    const variantConfigs = getVariantConfigs(gt);
    const config = variantConfigs[variant];
    const IconComponent = config.icon;
    const isA11yLabelActive =
        useViewjobProctorAllocation(MobVjMatchQualificationA11yLabel).value > 0;

    const accessibilityFocusRef = useRef<BoxRef>(null);
    const confirmButtonRef = useRef<BoxRef>(null);
    const undoButtonRef = useRef<BoxRef>(null);

    useImperativeHandle(
        ref,
        () => ({
            // Splits focus across two targets for a11y techs
            // - Screen readers (TalkBack/VoiceOver) focus the row's icon.
            // - Keyboards focus the actionable Confirm/Undo button.
            focus: () => {
                if (accessibilityFocusRef.current) {
                    setUniversalFocus(accessibilityFocusRef, {
                        setAccessibilityFocus: true,
                        setKeyboardFocus: false,
                        delay: 0,
                    });
                }

                const interactiveRef = onUndo ? undoButtonRef : confirmButtonRef;
                if (interactiveRef.current) {
                    setUniversalFocus(interactiveRef, {
                        setAccessibilityFocus: false,
                        setKeyboardFocus: true,
                        delay: 0,
                    });
                }
            },
        }),
        [onUndo],
    );

    return (
        <Flex
            sx={{
                flexDirection: 'row',
                gap: 8,
                alignItems: 'center',
                marginBlockStart: 8,
                flexWrap: 'nowrap',
                minHeight: 32,
            }}
        >
            <Box
                ref={accessibilityFocusRef}
                accessible={true}
                accessibilityLabel={config.iconAriaLabel}
                accessibilityRole="image"
                sx={{
                    width: 24,
                    height: 24,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    flexBasis: 24,
                    backgroundColor: config.backgroundColor,
                    borderRadius: 8,
                }}
            >
                <IconComponent color={config.iconColor} size="xs" />
            </Box>

            <Text
                level={2}
                sx={{
                    // No flexWrap here: this Text is a flex *item* (flexGrow/
                    // flexBasis position it in the parent row); its text content
                    // wraps via normal text layout. flexWrap only affects a flex
                    // *container*, so it's a no-op in RN and triggers a
                    // "display:flex required" error under RSD on a span.
                    flexGrow: 1,
                    flexBasis: 0,
                }}
            >
                <Text
                    sx={{
                        color: config.textColor,
                        fontWeight: '500',
                    }}
                >
                    {primaryText}
                </Text>
                <Text
                    sx={{
                        color: config.textColor,
                        fontStyle: 'italic',
                    }}
                >
                    {required && ` (${gt.gettext('required')})`}
                </Text>
                {onUndo && (
                    <>
                        {'\n'}
                        <Text level={1}>{gt.gettext('Saved to your profile.')}</Text>
                    </>
                )}
            </Text>

            {variant === MatchQualificationVariant.Missing && !onUndo && (
                <Flex sx={styles.buttonGroup}>
                    <Button
                        ref={confirmButtonRef}
                        variant="tertiary"
                        onPress={onConfirm}
                        ariaLabel={
                            isA11yLabelActive
                                ? gt.gettext('Confirm qualification: {0}', [primaryText])
                                : gt.gettext('Confirm qualification')
                        }
                        iconAfter={
                            <Check size="xs" fill={colors.background.status.success['[base]']} />
                        }
                        iconOnly
                    />
                    <Divider vertical />
                    <Button
                        variant="destructiveTertiary"
                        onPress={onReject}
                        ariaLabel={
                            isA11yLabelActive
                                ? gt.gettext('Reject qualification: {0}', [primaryText])
                                : gt.gettext('Reject qualification')
                        }
                        iconAfter={
                            <Close
                                size="xs"
                                fill={colors.background.action.destructive['[base]']}
                            />
                        }
                        iconOnly
                    />
                </Flex>
            )}
            {onUndo && (
                <Button ref={undoButtonRef} variant="tertiary" onPress={onUndo}>
                    {gt.gettext('Undo')}
                </Button>
            )}
        </Flex>
    );
}

const styles = StyleSheet.create({
    buttonGroup: {
        flexDirection: 'row',
        flexShrink: 0,
        flexBasis: 78,
        alignSelf: 'flex-end',
        borderWidth: 1,
        borderColor: colors.border.weak,
        borderRadius: borderRadius.md,
    },
});
